/* eslint-disable  @typescript-eslint/no-explicit-any */
import { Component, Event, h, Host, Listen, Prop, State } from '@stencil/core';
import {
  combineLatest,
  filter,
  first,
  map,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import type { FacetTreeUiModel } from '../../../models/ui/facets/facet-tree-ui.model';
import { cloneObjectWithNoReference } from '../../../utils/helpers/clone-object-with-no-reference';
import type { FilterNode } from './filter-node';
import { FacetsV2Service, type FilterNodeRequestUnique } from '../../../utils/services/facets/facets-v2.service';
import { makeLogger } from '../../../logger/logger';
import { isMobile } from '../../../utils/media';
import type { CoseRequestService } from '../../../utils/services/cose/cose-request.service';
import {facetsDataCreator} from './utils/facets-data-creator';
import {FilterChangeContext} from './models/filter-change-context.enum';
import type {ChipData} from '../../../models/common/chip-data.model';
import type {
  NewFacetsLogicConfig
} from '../../strategies/types/facets-config-strategy-data.type';
import {DEFAULT_FACET_LOGIC_CONFIG_CONSTANT} from './constants/default-facet-logic-config.constant';
import type {FacetChildUiModel} from '../../../models/ui/facets/facet-tree-ui.model';
import type { labelProviderInterface } from '../../LabelProviderInterface';

const { info, warn, debug } = makeLogger({
  pathToCurrentFile: 'components/result-page/facets-build-wrapper/facets-build-wrapper.tsx',
  loggersList: ['info', 'warn', 'debug']
});

export interface FacetsData {
  facets?: FacetTreeUiModel[]
  facetsChipsState?: FilterNode[]
  facetsState?: FilterNodeRequestUnique[]
  filteredFacets?: FacetTreeUiModel[]
  outerFacets?: FacetChildUiModel[]
  filteredMobileFacets?: FacetTreeUiModel[]
  outerMobileFacets?: FacetTreeUiModel[]
  onlyLastChilds?: FacetTreeUiModel[] | FacetChildUiModel[]
  flatFacetsList?: FacetTreeUiModel[] | FacetChildUiModel[]
  facetLocalChipsState?: FilterNode[]
  notLastUniqueIds: Record<string, string[]>
  totalHits?: number | null;
  previewsStateData?: any
}

@Component({
  tag: 'uiaas-search-facets-build-wrapper',
  styleUrl: './facets-build-wrapper.scss',
  shadow: false,
  scoped: true
})
export class FacetsBuildWrapper {
  private unsubscriber$: Subject<any> = new Subject<any>();
  private mobileFiltersWrapperElement?: HTMLElement | any;

  @Prop({ mutable: true}) labelProvider?: labelProviderInterface;
  @Prop() readonly rtl: boolean;
  @Prop() readonly query: string;
  @Prop({ mutable: false, attribute: 'hidden-facets' }) readonly hiddenFacets: string[] = [];
  @Prop() readonly coseRequestService?: CoseRequestService;
  @Prop( ) readonly facetsService?: FacetsV2Service;
  @Prop() readonly totalHitsProp?: string;
  @Prop() readonly tabName: string;
  @Prop() readonly isMobileFiltering: boolean = false;
  @Prop() readonly isNewFacetsLogic: boolean = false;
  @Prop() readonly facetsLabel?: string;
  @Prop({ attribute: 'facets-logic-config'}) readonly facetsLogicConfig: NewFacetsLogicConfig = DEFAULT_FACET_LOGIC_CONFIG_CONSTANT;
  @Prop() readonly filterPremiumIconUniqueId?: string;
  @Prop() readonly collapseCharactericticsBtnType?: string;
  @Prop() readonly headerAdditionalComponents?: {element: (param: boolean) => HTMLElement, elementHeight: number};

  @State() facetsDataState: FacetsData;
  @State() mobileView: boolean = isMobile();

  private headerSelector: string;
  private dividerMobileFacets: string[];
  private dividerFacets: string[];
  private flatFacets: boolean = false;
  private defaultFiltresWrapperElement;
  private filterButtonType: string = 'default';

  get isMobileMobileFilteringActive(): boolean {
      return (this.isMobileFiltering && this.facetsLogicConfig && this.facetsLogicConfig.mobileFilteringConfig && !!this.facetsDataState);
  }

  get isDefaultFacetsView(): boolean {
      return (this.isNewFacetsLogic && this.facetsLogicConfig && !!this.facetsDataState);
  }

  @Listen('on:chip:remove')
  handleCloseChip(event: CustomEvent) {
      this.facetsService.skipWatchOnCount = false;
      const eventDetail: ChipData = event.detail;
      const filteredNodes = this.facetsDataState.facetsChipsState.filter((chip: FilterNode) => eventDetail.id === chip.uniqueId);
      if(filteredNodes.length !== 0) {
        const filterNode: FilterNode = {...filteredNodes[0]};
        filterNode.selected = false;
        this.facetsService.clearFacet(filterNode);
      }
  }

  @Listen('filter:did:change')
  handleFilterDidChange(event: CustomEvent) {
      const {filterNode, context} = event.detail;

      if(context === FilterChangeContext.desktop) {
        this.facetsService.currentContext = context;
        this.facetsService.addOrRemoveChipOnState(filterNode, filterNode.selected);
        this.facetsService.onFacetChange(filterNode, this.facetsDataState.facetsState, this.facetsDataState.facets, false, this.dividerFacets);
        this.coseRequestService.initDefaultPipe(undefined, true);
        return;
      }

      if(context !== FilterChangeContext.mobile) {
        this.facetsService.currentContext = context;
        this.facetsService.onFacetChange(filterNode, this.facetsDataState.facetsState, this.facetsDataState.facets, false, this.dividerMobileFacets);
      }
  }

  @Listen('filters:did:change')
  handleFiltersDidChange(event: CustomEvent) {
      const {filterNodes, context} = event.detail;

      if(context === FilterChangeContext.desktop) {
        this.facetsService.currentContext = context;
        filterNodes.forEach(filterNode => {
            this.facetsService.addOrRemoveChipOnState(filterNode, filterNode.selected);
            this.facetsService.onFacetChange(filterNode, this.facetsDataState.facetsState, this.facetsDataState.facets);
        });
        this.coseRequestService.initDefaultPipe(undefined, true);
        return;
      }
  }

  @Listen('search:reset')
  onReset() {
      debug('search:reset___in___facest-wrapper');
  }

  componentWillLoad() {
      if (!this.labelProvider) {
        info('Assigning default labelProvider to reset button component');
        this.labelProvider = (name, fallback) => {
            warn(
              'LabelProvider not provided in component',
              [name, fallback]
            );
            return fallback;
        };
      }

      if(this.facetsLogicConfig) {
        this.dividerFacets = this.facetsLogicConfig.dividerFacets;
      }

      if(this.facetsLogicConfig && this.facetsLogicConfig?.mobileFilteringConfig) {
        this.flatFacets = this.facetsLogicConfig.mobileFilteringConfig?.flatFacets !== undefined ? this.facetsLogicConfig.mobileFilteringConfig.flatFacets : false;
        this.headerSelector = this.facetsLogicConfig.mobileFilteringConfig.headerSelector;
        this.dividerMobileFacets = this.facetsLogicConfig.mobileFilteringConfig.dividerMobileFacets;
        this.filterButtonType = this.facetsLogicConfig.mobileFilteringConfig?.filterButtonType || 'default';
      }

      this.onFacetChanges();
      this.onClearFacet();
      this.onChipRemoved();
      this.onCheckboxInActiveState();
  }

  disconnectedCallback() {
      this.unsubscriber$.next(null);
      this.unsubscriber$.complete();
  }

  render() {
      return (
        <Host>
          {(this.isMobileMobileFilteringActive) ?
            <uiaas-search-mobile-filters-analytics
              coseRequestService={this.coseRequestService}
              store={this.facetsService.createMobileAnalyticsStore()}
              tabName={this.tabName}
            >
              <mobile-filters-wrapper
                ref={(e) => {this.mobileFiltersWrapperElement = e;}}
                hiddenFacets={this.hiddenFacets}
                headerSelector={this.headerSelector}
                tabName={this.tabName}
                labelProvider={this.labelProvider as labelProviderInterface}
                rtl={this.rtl}
                dividerMobileFacets={this.dividerMobileFacets}
                flatFacets={this.flatFacets}
                coseRequestService={this.coseRequestService}
                facetsService={this.facetsService}
                totalHitsProp={this.totalHitsProp}
                facetsDataProp={JSON.stringify(this.facetsDataState)}
                showFiltersSearchInputLimit={this.getShowFiltersSearchInputLimit()}
                desktopShowChildren={this.facetsLogicConfig.showChildren}
                headerAdditionalComponents={this.headerAdditionalComponents}
                filterButtonType={this.filterButtonType}
                fixedFilterHeader={this.facetsLogicConfig.mobileFilteringConfig.fixedFilterHeader}
              >
              </mobile-filters-wrapper>
            </uiaas-search-mobile-filters-analytics>
            : ''}
          {this.isDefaultFacetsView ?
            <uiaas-search-default-analytics-filters-wrapper tabName={this.tabName} store={this.facetsService.createDefaultAnalyticsStore()}>
              <default-filters-wrapper
                ref={(el) => this.defaultFiltresWrapperElement = el}
                hiddenFacets={this.hiddenFacets}
                tabName={this.tabName}
                facetsLabel={this.facetsLabel}
                labelProvider={this.labelProvider as labelProviderInterface}
                rtl={this.rtl}
                coseRequestService={this.coseRequestService}
                facetsService={this.facetsService}
                facetsDataProp={JSON.stringify(this.facetsDataState)}
                dividerFacets={this.dividerFacets}
                facetsLogicConfig={this.facetsLogicConfig}
                filterPremiumIconUniqueId={this.filterPremiumIconUniqueId}
                collapseCharactericticsBtnType={this.collapseCharactericticsBtnType}
                mobileView={this.mobileView}
                query={this.query}
              ></default-filters-wrapper>
            </uiaas-search-default-analytics-filters-wrapper>
            : ''}
        </Host>
      );
  }

  private getShowFiltersSearchInputLimit(): number | null {
      return this.facetsLogicConfig && this.facetsLogicConfig?.mobileFilteringConfig && this.facetsLogicConfig?.mobileFilteringConfig?.showFiltersSearchInputLimit
        ? this.facetsLogicConfig?.mobileFilteringConfig?.showFiltersSearchInputLimit
        : null;
  }

  private onFacetChanges(): void {
      combineLatest(
        [this.facetsService.facets,
          this.facetsService.facetsChipsState,
          this.facetsService.facetsState,
          this.facetsService.facetsMobileService.facetLocalChipsState]
      ).pipe(
        takeUntil(this.unsubscriber$),
        map(
          ([facets, facetsChipsState, facetsState, facetLocalChipsState]) =>
            facetsDataCreator(
              facets,
              this.dividerFacets,
              this.dividerMobileFacets,
              facetsChipsState,
              facetsState,
              facetLocalChipsState,
              undefined,
              this.facetsLogicConfig.showChildren,
              this.flatFacets,
              this.facetsService.currentContext)
        ),
        tap((facetsData: FacetsData) => {
            this.facetsService.notLastUniqueIds = this.facetsLogicConfig.showChildren === 'onlyLastChilds' ? facetsData.notLastUniqueIds || {} : {};
            this.facetsDataState = cloneObjectWithNoReference<FacetsData>(facetsData);
            const previewsStateData = cloneObjectWithNoReference(this.facetsDataState);
            this.facetsDataState.previewsStateData = previewsStateData;
        }),
      ).subscribe();
  }

  private setInActiveCheckboxesOnDefaultFilters(bool: boolean) {
      if(this.defaultFiltresWrapperElement) {
        this.defaultFiltresWrapperElement.shadowRoot.querySelectorAll('uiaas-search-facet-filters-node, single-facet-node').forEach((el) => {
            if (el.tagName.toLowerCase() === 'single-facet-node' ) {
              el?.setDisabled(bool);
            } else {
              el.shadowRoot.querySelectorAll('filter-checkbox').forEach((checkboxEl) => {
                  checkboxEl.setDisabled(bool);
              });
            }
        });
      }
  }

  private onClearFacet(): void {
      this.facetsService.onClearFacet
        .pipe(
          takeUntil(this.unsubscriber$),
          filter((value: FilterNode) => {
              const chipsState = this.facetsService.facetChipsState$.value;
              const index = chipsState.findIndex(chips => chips.uniqueId === value.uniqueId);
              return !!value && value.uniqueId && index !== -1;
          }),
          tap((value: FilterNode) => {
              this.facetsService.onFacetChange(value as unknown as FilterNode, this.facetsDataState.facetsState, this.facetsDataState.facets);
          }),
          switchMap(() => this.coseRequestService.initPipe(this.dividerMobileFacets))
        )
        .subscribe((data) => {
            this.facetsDataState.totalHits = data.stats.totalHits;
            if(this.facetsService.facetChipsState$.value.length === 0) {
              this.mobileFiltersWrapperElement.clearAllViewChange();
            }
        });
  }

  private onChipRemoved() {
      this.facetsService.chipRemoved
        .pipe(
          takeUntil(this.unsubscriber$),
          filter((value: FilterNode | null) => {
              return !!value;
          })
        ).subscribe((filterNode) => {
            this.facetsService.addOrRemoveChipOnState(filterNode, filterNode.selected);
            const dividerFacets = this.mobileView ? this.dividerMobileFacets : this.dividerFacets;
            this.facetsService.onFacetChange(filterNode, this.facetsDataState.facetsState, this.facetsDataState.facets, false, dividerFacets);
            this.coseRequestService.initDefaultPipe(undefined, true);
        });
  }

  private onCheckboxInActiveState() {
      this.facetsService.checkboxesInActiveState
        .pipe(
          takeUntil(this.unsubscriber$)
        ).subscribe((bool) => {
            this.setInActiveCheckboxesOnDefaultFilters(bool);
        });
  }
}
